import { createNamespacedHelpers } from 'vuex';
import { DefineActions, DefineGetters, DefineMutations } from 'vuex-type-helper';
import { Getters, Mutations, Actions, Loads, ExtraType } from '@/models/Load.model';
import dayjs from 'dayjs';

// Inital state
const state: Loads = {
  movementLoads: [],
  approvalLoads: [],
  customerRates: [],
  extraTypes: [],
};

// Gets the values of our state usually called from components
const getters: DefineGetters<Getters, Loads> = {
  // YAY! no complex nested objects to worry about here - https://github.com/vuejs/vue/issues/4292 complex nested objects are not reactive
  emptyLoad: (state) => {
    return {
      id: 0,
      statusId: 0,
      statusName: '',
      statusCode: '',
      customerId: 0,
      customerName: '',
      customerCode: '',
      isCustomerDeleted: false,
      from: '',
      to: '',
      rate: 0.00,
      driverId: 0,
      driverName: '',
      driverNickname: '',
      oldDriverId: 0,
      oldDriverName: '',
      oldDriverNickname: '',
      horseVehicleId: 0,
      horseVehicleCode: '',
      lastLocation: '',
      lastLocationDate: '',
      oldHorseVehicleId: 0,
      oldVehicleCode: '',
      trailer1VehicleId: 0,
      trailer1VehicleCode: '',
      trailer2VehicleId: 0,
      trailer2VehicleCode: '',
      orderNumber: '',
      startKm: 0,
      oldHorseStartKm: 0,
      endKm: 0,
      oldHorseEndKm: 0,
      swapLocation: '',
      totalKm: 0,
      isPriorityLoad: false,
      loadDate: dayjs().format('YYYY-MM-DD'),
      operatorId: 0,
      operatorName: '',
      criticalComment: '',
      finalPrice: 0,
      vehicleSuspended: false,
      invoiceNumber: '',
      invoiceDate: '',
    };
  },

  // YAY! no complex nested objects to worry about here - https://github.com/vuejs/vue/issues/4292 complex nested objects are not reactive
  emptyLoadStop: (state) => {
    return {
      id: 0,
      loadId: 0,
      appointmentTime: dayjs().format('YYYY-MM-DD HH:mm'),
      client: '',
      address: '',
    };
  },

  movementLoads: (state) => state.movementLoads,
  approvalLoads: (state) => state.approvalLoads,
  customerRates: (state) => state.customerRates,
  extraTypes: (state) => state.extraTypes,

  extraTypeById: (state) => (extraTypeId: number): ExtraType => {
    const foundIndex = state.extraTypes.findIndex((item: ExtraType) => item.id === extraTypeId);

    if (foundIndex >= 0) { 
      return state.extraTypes[foundIndex];
    } else {
      return {
        id: 0,
        type: '',
        icon: '',
      };
    }
  },
};

// TODO: Change mutation names to uppercase ( Standards )
// Sets values or changes to state. Must be called via an action.
const mutations: DefineMutations<Mutations, Loads> = {
  setMovementLoads(state, { loads }) {
    state.movementLoads = loads;
  },

  setApprovalLoads(state, { loads }) {
    state.approvalLoads = loads;
  },

  setCustomerRates(state, { loads }) {
    state.customerRates = loads;
  },

  setExtraTypes(state, { extraTypes }) {
    state.extraTypes = extraTypes;
  },
};

// Async functions that will contain the logic / service calls and commit mutations to state
const actions: DefineActions<Actions, Loads, Mutations, Getters> = {
  setMovementLoads({ commit }, payload) {
    commit('setMovementLoads', payload);
  },

  setApprovalLoads({ commit }, payload) {
    commit('setApprovalLoads', payload);
  },

  setCustomerRates({ commit }, payload) {
    commit('setCustomerRates', payload);
  },

  setExtraTypes({ commit }, payload) {
    commit('setExtraTypes', payload);
  },
};

export const {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} = createNamespacedHelpers<Loads, Getters, Mutations, Actions>('LoadsModule');

export const LoadsModule = {
  namespaced: true,
  state: state,
  getters: getters,
  mutations: mutations,
  actions: actions,
};
